/**
 * Follow Work activity, but give the reader control once one reply grows tall.
 * Measure the reply body separately from its tool trail: a hundred short tool
 * updates must not exhaust the reading allowance for a single answer.
 */
export function createWorkScrollFollower({ getMetrics, scrollTo, requestFrame, cancelFrame }) {
  let following = true;
  let frame = null;
  let replyKey = null;
  let replyId = null;
  let replyHeight = 0;
  let baselineHeight = 0;
  let baselineOnMeasure = true;
  let measured = false;
  let lastFrameTime = null;
  let stopAt = null;
  let readingAllowance = null;
  let positioning = null;

  const cancel = () => {
    if (frame !== null) cancelFrame(frame);
    frame = null;
    lastFrameTime = null;
  };
  const pause = () => {
    following = false;
    stopAt = null;
    positioning = null;
    cancel();
  };
  const tick = (time) => {
    frame = null;
    if (!following && stopAt === null) return;
    const { contentHeight, layoutHeight, offset, bottomInset = 0, tailClearance = 0, blocked = false } = getMetrics();
    if (blocked || layoutHeight <= 0) {
      lastFrameTime = null;
      return;
    }
    const maxOffset = Math.max(0, contentHeight - layoutHeight);
    if (positioning) {
      // Native FlatList only exposes the measured part of its tail. Keep
      // reaching that temporary bottom so subsequent batches can mount,
      // including the last reply whose measurement live following waits on.
      const geometry = [contentHeight, layoutHeight, replyId, replyHeight];
      if (positioning.startedAt === null) positioning.startedAt = time;
      if (!positioning.geometry || geometry.some((value, i) => value !== positioning.geometry[i])) {
        positioning.geometry = geometry;
        positioning.changedAt = time;
      }
      const settled = (!replyId || measured) && time - positioning.changedAt >= positioning.stabilizeMs;
      const expired = time - positioning.startedAt >= positioning.maxMs;
      if (!settled && !expired) {
        if (offset !== maxOffset) scrollTo(maxOffset);
        frame = requestFrame(tick);
        return;
      }
      positioning = null;
      // Images and tool trails that laid out while opening are history, not
      // newly streamed text that should spend the reader's allowance.
      baselineHeight = replyHeight;
      baselineOnMeasure = !measured;
      readingAllowance = null;
      lastFrameTime = null;
    }
    // Wait for the new body measurement before following a large message that
    // arrived in one batch. Native child/list layout callbacks can arrive in
    // either order; measureReply schedules another frame when it is ready.
    if (replyId && !measured) return;
    // Composer approval cards may appear/disappear without any reply growth.
    // Do not let their height changes retroactively exhaust this allowance.
    if (readingAllowance === null) {
      readingAllowance = Math.min(320, Math.max(80, (layoutHeight - bottomInset) * 0.5));
    }
    const excess = Math.max(0, replyHeight - baselineHeight - readingAllowance);
    let target = stopAt === null ? maxOffset : Math.min(stopAt, maxOffset);
    if (following && excess > 0) {
      target = Math.min(maxOffset, Math.max(offset, maxOffset - excess));
      if (target < maxOffset - 2) {
        following = false;
        stopAt = target;
      }
    }
    // One animation follows a moving target; token batches never restart a
    // native smooth-scroll animation or queue a second bottom pin.
    const dt = lastFrameTime === null ? 16 : Math.min(64, Math.max(1, time - lastFrameTime));
    lastFrameTime = time;
    const distance = target - offset;
    if (Math.abs(distance) <= 1) {
      if (distance !== 0) scrollTo(target);
      stopAt = null;
      lastFrameTime = null;
      return;
    }
    const eased = offset + distance * (1 - Math.exp(-dt / 55));
    // A newly mounted goal/access card or a burst of tool rows can move the
    // dock boundary faster than the animation. Reveal short live updates on
    // this frame, then ease through the remaining breathing room. Long replies
    // and manual reading retain their bounded/paused position.
    const visibleOffset = following && target === maxOffset && tailClearance > 0
      ? Math.max(0, maxOffset - tailClearance / 2)
      : 0;
    scrollTo(Math.max(eased, visibleOffset));
    frame = requestFrame(tick);
  };
  const schedule = () => {
    if ((following || stopAt !== null) && frame === null) frame = requestFrame(tick);
  };

  return {
    schedule,
    pause,
    isFollowing: () => following,
    // A reply's ordinal survives the draft -> persisted-id handoff. That
    // handoff must not replenish its allowance halfway through a long answer.
    setReply(key, id) {
      if (key !== replyKey && id !== replyId) {
        const firstReply = replyKey === null;
        replyKey = key;
        replyHeight = 0;
        baselineHeight = 0;
        readingAllowance = null;
        baselineOnMeasure = firstReply;
        measured = !id;
      }
      replyKey = key;
      if (id !== replyId) measured = !id;
      replyId = id;
    },
    measureReply(id, height) {
      if (id !== replyId || !Number.isFinite(height)) return;
      replyHeight = Math.max(0, height);
      measured = true;
      if (baselineOnMeasure) {
        baselineHeight = replyHeight;
        baselineOnMeasure = false;
      }
      schedule();
    },
    resume() {
      cancel();
      following = true;
      stopAt = null;
      positioning = null;
      baselineHeight = replyHeight;
      readingAllowance = null;
      baselineOnMeasure = !measured;
      schedule();
    },
    positionAtTail({ stabilizeMs = 600, maxMs = 4000 } = {}) {
      cancel();
      following = true;
      stopAt = null;
      positioning = { startedAt: null, changedAt: null, geometry: null, stabilizeMs, maxMs };
      schedule();
    },
    dispose: cancel,
  };
}
